import type { IButtonStyles } from '@fluentui/react';
import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import {
    createAppFlyoutAnchor,
    createGeneratedMenuItem,
    getKeytipString,
    constructKeytip,
} from 'owa-command-ribbon';
import type EditorViewState from 'owa-editor/lib/store/schema/EditorViewState';
import { callFormatApi } from 'owa-editor/lib/utils/callFormatApi';
import { getComposeRibbonId } from 'owa-editor/lib/utils/getComposeRibbonId';
import ChangeCase from 'owa-fluent-icons-svg/lib/icons/TextChangeCaseRegular';
import loc from 'owa-localize';
import { FormatControlId } from 'owa-ribbon-ids';
import { Capitalization } from 'roosterjs-editor-types';
import {
    capitalizeEachWordText,
    changeCaseText,
    lowercaseText,
    sentenceCaseText,
    uppercaseText,
} from './formatControlStrings.locstring.json';
import { isFeatureEnabled } from 'owa-feature-flags';

const onExecuteChangeCase = (viewState: EditorViewState | undefined, action: Capitalization) => {
    if (!!viewState) {
        callFormatApi(viewState, 'changeCapitalization', [action]);
    }
};

export const getChangeCaseControl = computedFn(
    (
        editorViewState: EditorViewState | undefined,
        styles: IButtonStyles | undefined,
        menuItemStyles: IButtonStyles | undefined,
        tabId: number | undefined
    ): RibbonControlProps => {
        return createAppFlyoutAnchor(
            getComposeRibbonId(FormatControlId.ChangeCase, editorViewState),
            loc(changeCaseText),
            {
                sections: [
                    {
                        controls: [
                            createGeneratedMenuItem(
                                getComposeRibbonId(FormatControlId.Uppercase, editorViewState),
                                loc(uppercaseText),
                                () =>
                                    onExecuteChangeCase(editorViewState, Capitalization.Uppercase),
                                tabId && isFeatureEnabled('mon-ribbon-keytips')
                                    ? constructKeytip(
                                          [
                                              getKeytipString(tabId),
                                              getKeytipString(FormatControlId.ChangeCase),
                                          ],
                                          getKeytipString(FormatControlId.Uppercase)
                                      )
                                    : undefined,
                                undefined /* icon */,
                                undefined /* iconColor */,
                                menuItemStyles
                            ),
                            createGeneratedMenuItem(
                                getComposeRibbonId(FormatControlId.Lowercase, editorViewState),
                                loc(lowercaseText),
                                () =>
                                    onExecuteChangeCase(editorViewState, Capitalization.Lowercase),
                                tabId && isFeatureEnabled('mon-ribbon-keytips')
                                    ? constructKeytip(
                                          [
                                              getKeytipString(tabId),
                                              getKeytipString(FormatControlId.ChangeCase),
                                          ],
                                          getKeytipString(FormatControlId.Lowercase)
                                      )
                                    : undefined,
                                undefined /* icon */,
                                undefined /* iconColor */,
                                menuItemStyles
                            ),
                            createGeneratedMenuItem(
                                getComposeRibbonId(
                                    FormatControlId.CapitalizeEachWord,
                                    editorViewState
                                ),
                                loc(capitalizeEachWordText),
                                () =>
                                    onExecuteChangeCase(
                                        editorViewState,
                                        Capitalization.CapitalizeEachWord
                                    ),
                                tabId && isFeatureEnabled('mon-ribbon-keytips')
                                    ? constructKeytip(
                                          [
                                              getKeytipString(tabId),
                                              getKeytipString(FormatControlId.ChangeCase),
                                          ],
                                          getKeytipString(FormatControlId.CapitalizeEachWord)
                                      )
                                    : undefined,
                                undefined /* icon */,
                                undefined /* iconColor */,
                                menuItemStyles
                            ),
                            createGeneratedMenuItem(
                                getComposeRibbonId(FormatControlId.SentenceCase, editorViewState),
                                loc(sentenceCaseText),
                                () => onExecuteChangeCase(editorViewState, Capitalization.Sentence),
                                tabId && isFeatureEnabled('mon-ribbon-keytips')
                                    ? constructKeytip(
                                          [
                                              getKeytipString(tabId),
                                              getKeytipString(FormatControlId.ChangeCase),
                                          ],
                                          getKeytipString(FormatControlId.SentenceCase)
                                      )
                                    : undefined,
                                undefined /* icon */,
                                undefined /* iconColor */,
                                menuItemStyles
                            ),
                        ],
                    },
                ],
            },
            tabId && isFeatureEnabled('mon-ribbon-keytips')
                ? constructKeytip(
                      [getKeytipString(tabId)],
                      getKeytipString(FormatControlId.ChangeCase),
                      true
                  )
                : undefined,
            ChangeCase,
            undefined /* iconColor */,
            styles
        );
    }
);
